import {Component, ViewEncapsulation} from '@angular/core';
import {Capacitor} from '@capacitor/core';
import {ApiService} from '../../../../shared/services/api.service';
import {EnrollService} from '../../../../shared/services/enroll.service';
import {DevicesService, DialogLoadingData, LangService, LoaderService, NavigateService} from '@satoris/ngx-satoris';
import {ScanService} from 'src/app/shared/services/scan.service';
import {NfcService} from 'src/app/shared/services/nfc.service';
import {TimeoutError} from 'rxjs';
import {ClaimType} from 'src/app/shared/models/claim';
import {FaceDetector, FilesetResolver} from '@mediapipe/tasks-vision';

@Component({
  selector: 'app-activation-step-take-face-picture',
  templateUrl: './activation-step-take-face-picture.component.html',
  styleUrls: ['./activation-step-take-face-picture.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class ActivationStepTakeFacePictureComponent {
  optionsCamera = {
    x: 0,
    y: 0,
    width: window.screen.width,
    height: window.innerHeight - (this.api.userRole.isAdmin ? 180 : 100),
    camera: 'front',
    toBack: false,
    tapPhoto: Capacitor.getPlatform() === 'ios',
    tapFocus: true,
    previewDrag: false,
    storeToFile: false,
    disableExifHeaderStripping: true
  };

  cameraReady = false;
  detectorReady = false;
  pictureProcessing = false;
  picture: string = '';
  imageQuality = 1;
  faceDetector: Promise<FaceDetector>;
  private destroyed = false;

  constructor(public api: ApiService,
    public enroll: EnrollService,
    public device: DevicesService,
    private nfc: NfcService,
    private loader: LoaderService,
    private scan: ScanService,
    private nav: NavigateService,
    private lang: LangService) {
    if(this.api.userInfo.nationalNumber && this.api.userRole.isCustomer) {
      this.api.setPrincipalMode = true;
    }
    this.initDetector();
  }

  private createDetector(): Promise<FaceDetector> {
    return FilesetResolver.forVisionTasks('assets/mediapipe/wasm').then((vision) => FaceDetector.createFromOptions(vision, {
      baseOptions: {
        modelAssetPath: 'assets/mediapipe/models/blaze_face_short_range.tflite'
      },
      runningMode: 'IMAGE',
      minDetectionConfidence: 0.6
    }));
  }

  private initDetector() {
    this.loader.loading(true);
    this.faceDetector = this.createDetector();
    this.faceDetector.then(() => {
      if(this.destroyed) {
        return;
      }
      this.detectorReady = true;
      this.loader.loading(false);
    }).catch(reason => {
      console.error('Face detector init failed', reason);
      if(this.destroyed) {
        return;
      }
      this.loader.loading(true, {type: 'error', message: 'err.faceDetector.load', btnLabel: 'retry', custom: {closeBtnLabel: 'cancel'}})
        .then((retry: boolean) => retry ? this.initDetector() : this.backToFaceInfo());
    });
  }

  ngOnInit() {
    if(this.api.userRole.isAdmin && (!this.scan.userScanData || !this.scan.userScanData?.data?.sig)) {
      this.loader.loading(true, {type: 'error', message: this.lang.transform('api.createClaim.missingData')}).then(() => {
        this.nav.to('user');
      });
    }
  }

  ngOnDestroy() {
    this.destroyed = true;
    this.api.setPrincipalMode = false;
  }

  /* giving up on the face check belongs on its explanation screen; counting steps back landed on the card scan, which re-reads the card */
  private backToFaceInfo() {
    if(!this.enroll.backTo(this.enroll.step.DESC_TAKE_FACE_PICTURE)) this.enroll.navigate('previous');
  }

  setPicture(imageUrl: string) {
    if(!this.detectorReady) {
      return;
    }
    const img = new Image();
    img.onload = () => {
      this.faceDetector.then((faceDetector) => {
        const {detections} = faceDetector.detect(img);
        if(detections.length === 1) {
          this.picture = imageUrl;
        } else {
          this.loader.loading(true, {
            message: `${detections.length} ${this.lang.transform('faces.detected')}`
          });
        }
      }).catch(reason => {
        console.error('Face detection failed', reason);
        this.loader.loading(true, {type: 'error', message: 'err.faceDetector.load'});
      });
    };
    img.src = imageUrl;
  }

  onConfirm() {
    if(!this.nfc.pkiCert) {
      if(this.api.env.enrollFormEasy) {
        this.enroll.form.controls[this.enroll.fc.FACE_PICTURE].setValue(this.picture);
      }
      this.enroll.navigate('next');
    } else if(this.picture) {
      this.enroll.form.controls[this.enroll.fc.FACE_PICTURE].setValue(this.picture);
      this.checkPin();
    } else {
      this.close();
    }
  }

  showPinDialog() {
    const showPinOptions: DialogLoadingData = {
      type: 'info',
      custom: {
        disableClose: true,
        innerHtml: this.lang.transform('getPin.faceCheck')
      }
    };
    this.loader.loading(true, showPinOptions).then(() => {
      this.loader.loading(true, {type: 'info', message: 'info.getPin.valid'}).then(() => this.enroll.navigate('next'));
    });
  }

  checkPin() {
    this.loader.loading(true);

    let claimType: ClaimType;
    switch(this.nfc.icao.mrz.documentCode) {
      case 'I':  // Standard ICAO code for Identity Card
        claimType = ClaimType.ID_CARD;
        break;
      case 'IC': // ICAO standard for CEDEAO (African Union) cards
        claimType = ClaimType.CEDEAO_CARD;
        break;
      case 'IE': // ICAO standard for Resident permits
        claimType = ClaimType.RESIDENT_CARD;
        break;
      case 'IF': // ICAO standard for Alien registration cards
        claimType = ClaimType.ALIEN_CARD;
        break;
      default:
        claimType = ClaimType.ID_CARD;
        break;
    }

    this.api.getPin(this.nfc.icao.personalDetails.personalNumber, this.nfc.icao.mrz.documentNumber, this.enroll.form.controls[this.enroll.fc.FACE_PICTURE].value, claimType)
      .then(cardPin => {
        this.enroll.serverPin = cardPin;
        this.showPinDialog();
      })
      .catch((err: TimeoutError | string) => {
        console.error(err);
        if(typeof err === 'string'){
          if(err?.includes('InvalidImage')) {
            this.loader.loading(true, {type: 'error', message: 'err.getPin.invalidImage', btnLabel: 'retry', custom: {closeBtnLabel: 'cancel'}})
              .then((done: boolean) => {
                if(done) {
                  this.imageQuality = this.imageQuality - 0.25 > 0.25 ? this.imageQuality - 0.25 : 0.25;
                  this.close();
                } else this.backToFaceInfo();
              });
          } else {
            const message = (err.includes('client.extended.noFaceAuth') || err.includes('"Code":"2"')) ? 'err.getPin.noMatch' : this.lang.transform('error.basic') + '. ' + err;
            this.loader.loading(true, {type: 'error', message: message, btnLabel: 'global.yes', custom: {closeBtnLabel: 'global.no'}})
              .then((done: boolean) => done ? this.close() : this.backToFaceInfo());
          }
        } else {
          console.error(`err with object: ${err.message}`);
          this.close();
        }
      });
  }

  close() {
    this.picture = '';
    this.enroll.form.controls[this.enroll.fc.FACE_PICTURE].setValue('');
  }

  uploadImage(event: any) {
    //! 0.75 ratio between width and height (480x640)
    const file = event.target.files[0];
    const reader = new FileReader();

    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if(ctx === null) {
          throw 'NULL canvas context';
        }
        canvas.width = 480;
        canvas.height = 640;

        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        const resizedImageDataURL = canvas.toDataURL(file.type);

        this.picture = resizedImageDataURL;
        this.enroll.form.controls[this.enroll.fc.FACE_PICTURE].setValue(this.picture);
        event.target.value = '';
      };
      img.src = reader.result as string;
    };

    reader.readAsDataURL(file);
  }

}
